import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Download, Loader2, Printer, MousePointerClick, Hand, ArrowRightLeft, Award } from "lucide-react";
import TrialTimer from "@/components/TrialTimer";
import TrialFeedback from "@/components/TrialFeedback";
import Seo from "@/components/Seo";
import ArtworkZoom from "@/components/ArtworkZoom";
import { WAYS_TO_PLAY, PRICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WAY_ICONS = [MousePointerClick, Hand, ArrowRightLeft, Award];

const Trial = () => {
  const { token } = useParams();
  const [trial, setTrial] = useState(null);
  const [cards, setCards] = useState(null);
  const [error, setError] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/trials/${token}`);
        if (cancelled) return;
        setTrial(data);
        if (!data.expired) {
          const c = await axios.get(`${API}/trials/${token}/cards`);
          if (!cancelled) setCards(c.data.cards);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (error === "notfound") {
    return (
      <div className="bg-cream" data-testid="trial-notfound">
        <Seo title="Free Trial" noindex />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading font-bold text-3xl text-ink mb-4">Trial link not found</h1>
          <p className="text-lg text-ink/75 mb-8">
            This trial link doesn't match our records. It may have been typed incorrectly.
          </p>
          <Link to="/try-free" data-testid="trial-notfound-register-link" className="inline-flex items-center justify-center bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300">
            Start a Free Trial
          </Link>
        </div>
      </div>
    );
  }

  if (!trial) {
    return (
      <div className="bg-cream min-h-[50vh] flex items-center justify-center" data-testid="trial-loading">
        <Loader2 className="h-10 w-10 animate-spin text-sage" aria-hidden="true" />
      </div>
    );
  }

  if (trial.expired) {
    return (
      <div className="bg-cream" data-testid="trial-expired">
        <Seo title="Free Trial" noindex />
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-28 text-center">
          <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">Trial complete</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-ink mb-6">
            Thank you for trying UNSTUCK Seniors, {trial.facility}.
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-10">
            Your 14-day free trial has ended. If UNSTUCK brought meaningful moments to your residents, the
            Complete UNSTUCK Seniors Program — four individually packaged 54-card decks, printed
            instructions, UNSTUCK Bucks, and program packaging — is ready when you are.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link
              to="/seniors#program"
              data-testid="trial-expired-buy-button"
              className="inline-flex items-center justify-center bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
            >
              Shop the Complete Program — ${PRICES.seniorsProgram.toFixed(0)}
            </Link>
            <button
              type="button"
              onClick={() => setShowFeedback((v) => !v)}
              data-testid="trial-expired-feedback-button"
              className="inline-flex items-center justify-center border-2 border-sage text-sage hover:bg-sage hover:text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
            >
              Share Your UNSTUCK Experience
            </button>
          </div>
          {showFeedback && (
            <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10">
              <TrialFeedback trialToken={token} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trial-page">
      <Seo title="Free Trial" noindex />
      {/* HEADER */}
      <section className="bg-sage">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-20 text-center">
          <p className="text-sm uppercase tracking-widest font-semibold text-gold mb-3">
            Free 14-day trial
          </p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-white mb-4">
            Welcome, {trial.facility}
          </h1>
          <p className="text-lg sm:text-xl text-cream/90 max-w-2xl mx-auto mb-6">
            Your 14 sample UNSTUCK Seniors cards, instructions, printable Bucks, and 15-minute timer are
            all ready below.
          </p>
          <p className="inline-block bg-white/15 text-cream rounded-full px-5 py-2 text-base font-medium mb-6" data-testid="trial-days-remaining">
            Day {Math.max(1, 14 - trial.days_remaining + 1)} of 14 — {trial.days_remaining} {trial.days_remaining === 1 ? "day" : "days"} remaining
          </p>
          <p className="font-heading italic text-xl text-gold">
            "Created by an Activity Director. Designed with Every Resident in Mind."
          </p>
        </div>
      </section>

      {/* 4 WAYS TO PLAY */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-3">4 Ways to Play</h2>
          <p className="text-lg text-ink/80 mb-4 max-w-3xl">
            UNSTUCK Seniors was created for residents with different abilities, including those with
            limited mobility or speech. Residents can participate independently, with assistance, through
            staff-supported activities, or in groups — and if an activity doesn't fit someone's abilities
            or preferences, they may pass or trade the card.
          </p>
          <p className="text-lg text-ink/80 mb-4 max-w-3xl">
            For example, if a resident cannot use their arms, a caregiver may clean their glasses for them.
            If a resident welcomes prayer but cannot speak, someone may pray with or for them. These
            residents still receive participation credit and can earn UNSTUCK Bucks.
          </p>
          <p className="font-heading italic text-xl text-sage mb-10">
            Participation is about inclusion, not perfection.
          </p>
          <div className="max-w-3xl mx-auto mb-12">
            <ArtworkZoom
              src="/images/four-ways-artwork.jpg"
              alt="Official UNSTUCK Seniors artwork: How to Play and the 4 Ways to Play — Solo Reset, Caregiver Assist, Group Play, and Bucks + Rewards"
              testId="trial-four-ways-artwork"
            />
            <p className="text-base text-ink/60 mt-3 text-center">
              Your printable instructions download below is this exact guide.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="trial-ways-to-play">
            {WAYS_TO_PLAY.map((way, i) => {
              const Icon = WAY_ICONS[i];
              return (
                <div key={way.name} className="bg-white rounded-2xl p-6 border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] flex gap-4 items-start">
                  <div className="h-11 w-11 shrink-0 rounded-full bg-gold-light flex items-center justify-center">
                    <Icon className="h-5 w-5 text-gold-dark" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-sage mb-1">{way.name}</h3>
                    <p className="text-base leading-relaxed text-ink/75">{way.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIMER */}
      <section className="bg-sage-light">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-3 text-center">Your 15-minute timer</h2>
          <p className="text-lg text-ink/75 mb-10 text-center">
            Pull a card, press start, and give the activity a friendly 15-minute window.
          </p>
          <TrialTimer />
        </div>
      </section>

      {/* 14 SAMPLE CARDS */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-3">Your 14 sample cards</h2>
          <p className="text-lg text-ink/75 mb-10 max-w-3xl">
            All 14 cards are available right now, for your whole trial. Cards are viewed here on screen —
            the printable downloads are the instructions and the sample UNSTUCK Bucks below.
          </p>
          {cards && cards.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="trial-cards-grid">
              {cards.map((card) => (
                <div
                  key={card.number}
                  data-testid={`trial-card-${card.number}`}
                  className="bg-white rounded-2xl border-2 border-gold/50 shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-7 flex flex-col min-h-[240px]"
                >
                  <p className="text-xs uppercase tracking-widest font-semibold text-gold-dark mb-1">
                    {card.category} • 15 minutes
                  </p>
                  <p className="text-xs uppercase tracking-widest font-semibold text-ink/40 mb-3">
                    UNSTUCK Seniors — sample card {card.number} of 14
                  </p>
                  <h3 className="font-heading font-bold text-2xl text-ink mb-3">{card.title}</h3>
                  <p className="text-lg leading-relaxed text-ink/80">{card.hint}</p>
                  <p className="text-lg leading-relaxed text-sage font-medium flex-1">{card.encouragement}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border-2 border-dashed border-sage/50 bg-sage-light p-10 text-center" data-testid="trial-cards-pending">
              <p className="font-heading font-bold text-xl text-sage mb-2">The 14 sample cards are being added</p>
              <p className="text-base text-ink/70 max-w-xl mx-auto">
                Tammi is placing the real cards from the 54-card UNSTUCK Seniors deck here. Everything else
                in your trial — instructions, Bucks sheet, and timer — is ready below.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* PRINTABLE MATERIALS */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-3">Printable materials</h2>
          <p className="text-lg text-ink/75 mb-10 max-w-3xl">
            Print these for your activity room. UNSTUCK Bucks are an optional reward — game pieces only,
            no cash value — and you choose your own rewards. No start times, end times, or minutes need to
            be recorded; participation matters more than the clock.
          </p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            {[
              { key: "instructions", title: "Four Ways to Use + Bucks Guide", tone: "sage" },
              { key: "bucks", title: "Printable $1 UNSTUCK Bucks Sheet", tone: "gold" },
              { key: "tracker", title: "Participation & Progress Tracker", tone: "sage" },
              { key: "tracker_guide", title: "Understanding Your Tracker", tone: "gold" },
            ].map((doc) => (
              <a
                key={doc.key}
                href={`${API}/trial-downloads/${token}/${doc.key}`}
                data-testid={`trial-download-${doc.key}`}
                className="bg-cream rounded-2xl border border-border p-7 flex items-center gap-4 hover:border-sage transition-colors group"
              >
                <div className={`h-12 w-12 shrink-0 rounded-full flex items-center justify-center ${doc.tone === "gold" ? "bg-gold-light" : "bg-sage-light"}`}>
                  <Printer className={`h-6 w-6 ${doc.tone === "gold" ? "text-gold-dark" : "text-sage"}`} aria-hidden="true" />
                </div>
                <div>
                  <p className="font-heading font-bold text-xl text-ink group-hover:text-sage transition-colors">{doc.title}</p>
                  <p className="text-base text-ink/70 inline-flex items-center gap-1"><Download className="h-4 w-4" aria-hidden="true" /> Printable PDF</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FEEDBACK */}
      <section id="feedback" className="bg-sage-light scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          {!showFeedback ? (
            <div className="text-center">
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-4">How is it going?</h2>
              <p className="text-lg text-ink/75 mb-8">
                Honest feedback — good or bad — helps us serve communities better.
              </p>
              <button
                type="button"
                onClick={() => setShowFeedback(true)}
                data-testid="share-experience-button"
                className="inline-flex items-center justify-center bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-8 py-4 text-lg font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-sage focus:ring-offset-2"
              >
                Share Your UNSTUCK Experience
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10">
              <h2 className="font-heading font-bold text-3xl text-ink mb-2 text-center">Share Your UNSTUCK Experience</h2>
              <p className="text-base text-ink/60 mb-8 text-center">{trial.facility}</p>
              <TrialFeedback trialToken={token} />
            </div>
          )}
        </div>
      </section>

      {/* PURCHASE CTA */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-20 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-4">
            Enjoying the trial?
          </h2>
          <p className="text-lg text-ink/75 max-w-2xl mx-auto mb-8">
            The Complete UNSTUCK Seniors Program includes all 54 cards in four individually packaged decks,
            printed instructions, UNSTUCK Bucks, and program packaging.
          </p>
          <Link
            to="/seniors#program"
            data-testid="trial-buy-program-button"
            className="inline-flex items-center justify-center bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 shadow-[0_2px_10px_rgba(74,93,78,0.2)]"
          >
            Shop the Complete Program — ${PRICES.seniorsProgram.toFixed(0)}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Trial;
